import React, { useState } from 'react'
import { motion } from "framer-motion";
import styled from 'styled-components';
import { isMobile } from 'react-device-detect';

const colors = {
	top: ["#dba008", "#df2d8e"],
	right: ["magenta", "cyan"],
	bot: ["cyan", "green"],
	left: ["#7b4397 0%, #7b4397 25%", "#dc2430 75%, #dc2430 100%"],
};

const ProjectContentVariants = {
	hidden: {
		opacity: 0,
	},
	visible: {
		opacity: 1,
		transition: {
			duration: 0.5,
		},
	},
    exit: {
        opacity: 0,
        transition: {
            duration: 0.5,
        },
    }
};

const Container = styled(motion.div)`
    width: 100%;
    display: flex;
    flex-direction: row;

    @media (max-width: 800px) {
        flex-direction: column;
        gap: 30px;
    }
`

const VertContainer = styled(motion.div)`
    width: 100%;
    display: flex;
    flex-direction: column;
    padding-left: 30px;

    @media (max-width: 800px) {
        padding-left: 0;
    }
`

const MenuContainer = styled(motion.div)`
    width: 33%;
    display: flex;
    flex-direction: row;

    @media (max-width: 800px) {
        width: 100%;
        flex-direction: column;
    }
`

const AbsoluteWrapper = styled(motion.div)`
    position: ${props => props.enabled ? "absolute" : "relative"};
`

const ContentContainer = styled(motion.div)`
    width: 85%;
    min-height: 250px;
    display: flex;

    @media (max-width: 800px) {
        width: 100%;
    }

    ${props => {
        if (!props.smallDevice) return `
            &:before {
                content: "";
                position: absolute;
                height: 230px;
                width: 4px;
                transform: translateX(3px);
                background-color: white;
                opacity: 0.2;
                border-radius: 3px;
            }
        `
    }}
`

const MenuItemContainer = styled(motion.div)`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 10px;
    cursor: pointer;

    @media (max-width: 800px) {
        margin-top: 6px;
    }
`

const MenuText = styled(motion.text)`
    font-family: Outfit;
    font-weight: 400;
    font-size: 24px;
    color: white;
    opacity: ${props => props.selected ? 0.9 : 0.6};

    &:hover {
        opacity: ${props => props.selected ? 0.9 : 0.7};
    }

    @media (max-width: 800px) {
        font-size: 18px;
    }
`

const Text = styled(motion.text)`
	font-family: Outfit;
	font-size: 22px;
	font-weight: 300;
	color: #FCFAFB;
	opacity: ${props => props.opacity ?? 1};

    @media (max-width: 800px) {
        font-size: 16px;
    }
`

const Heading = styled(motion.text)`
	font-family: Inter;
	font-weight: bold;
	font-size: 32px;
	color: white;
	opacity: 1;

    @media (max-width: 800px) {
        font-size: 22px;
    }
`

const Spacer = styled(motion.div)`
	height: ${props => props.height ?? 20}px;
`
const SpanRainbow = styled(motion.span)`
	font-family: inherit;
	font-size: inherit;
	font-weight: ${props => {
        if (props.bold) return "500;"
        else return "inherit;"
    }};

	${props => {
		let gradientColors = props.colors ?? colors["top"];
		let start = props.start ?? "bottom right";
		return `background: -webkit-linear-gradient(${start}, ${gradientColors[0]}, ${gradientColors[1]});`;
	}}
	background-clip: text;
	text-fill-color: transparent;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
`

const SpanPink = styled(motion.span)`
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    color: #fd71cd;
    transition-duration: 0.1s;
    cursor: pointer;

    &:hover {
        color: #e33daa;
    }
`

const Microtrader = () => {
    return (
        <VertContainer key={"Microtrader"} variants={ProjectContentVariants} initial="hidden" animate="visible" exit="exit">
            <Heading>Microtrader</Heading>
            <Text opacity={0.6}>Jan 2026 – Present</Text>
            <Spacer height={20} />
            <Text>
                A <SpanRainbow bold>Rust</SpanRainbow> engine for executing trades on the open market through Alpaca with sub-ms local latency.
                Supports paper-trading, backtesting, and realtime P&L visualization.
            </Text>
            <Spacer height={20} />
            <Text>
                Backed by a network of private algorithms — from{" "}
                <SpanRainbow bold>time-series models</SpanRainbow> to bar heuristics — that emit trade events via{" "}
                <SpanRainbow bold>gRPC</SpanRainbow> and <SpanRainbow bold>asyncio</SpanRainbow> Python services,
                persisted to <SpanRainbow bold>PostgreSQL</SpanRainbow>.
            </Text>
            <Spacer height={20} />
            <Text>
                <SpanPink onClick={() => window.open("https://github.com/azolyomi/microtrader")}>
                    github.com/azolyomi/microtrader
                </SpanPink>
            </Text>
        </VertContainer>
    )
}

const PatinaCompiler = () => {
    return (
        <VertContainer key={"PatinaCompiler"} variants={ProjectContentVariants} initial="hidden" animate="visible" exit="exit">
            <Heading>Patina Compiler</Heading>
            <Text opacity={0.6}>March – June 2023</Text>
            <Spacer height={20} />
            <Text>
                Built a register-based <SpanRainbow bold>compiler</SpanRainbow> for the Patina language from scratch in{" "}
                <SpanRainbow bold>OCaml</SpanRainbow>, including an AST module, tokenizer, regex-to-NFA converter,
                lexer, parser, type checker, code generator, and optimizations.
            </Text>
        </VertContainer>
    )
}

const Starship = () => {
    return (
        <VertContainer key={"Starship"} variants={ProjectContentVariants} initial="hidden" animate="visible" exit="exit">
            <Heading>Starship</Heading>
            <Text opacity={0.6}>2026</Text>
            <Spacer height={20} />
            <Text>
                A <SpanRainbow bold>Rust</SpanRainbow> platform for coordinating large-scale MMO raids across Discord — the successor to an earlier bot of mine that grew to{" "}
                <SpanRainbow bold>70,000+</SpanRainbow> users across <SpanRainbow bold>70+</SpanRainbow> communities.
            </Text>
            <Spacer height={20} />
            <Text>
                Handles membership, event organization, and moderation; designed in Rust for memory safety and high-concurrency throughput.
            </Text>
            <Spacer height={20} />
            <Text>
                <SpanPink onClick={() => window.open("https://github.com/azolyomi/starship")}>
                    github.com/azolyomi/starship
                </SpanPink>
            </Text>
        </VertContainer>
    )
}

const MedMaps = () => {
    return (
        <VertContainer key={"MedMaps"} variants={ProjectContentVariants} initial="hidden" animate="visible" exit="exit">
            <Heading>MedMaps</Heading>
            <Text opacity={0.6}>June 2020 – Present</Text>
            <Spacer height={20} />
            <Text>
                A medical pocket dictionary built with{" "}
                <SpanRainbow bold>React Native</SpanRainbow>, leveraged by{" "}
                <SpanRainbow bold>2,000+</SpanRainbow> medical students and faculty in daily patient visits.
            </Text>
            <Spacer height={20} />
            <Text>
                <SpanPink onClick={() => window.open("https://apps.apple.com/us/app/medmaps/id1519324957")}>
                    Available on the App Store
                </SpanPink>
            </Text>
        </VertContainer>
    )
}

export default function FeaturedProjectsMenu({ colors }) {
    const [selectedItem, setSelectedItem] = useState(0);
    const [renderAnimation, setRenderAnimation] = useState(true);

    const smallDevice = (isMobile || window.innerWidth < 800);

    // [ Name, Component ]
    const MenuItems = [
        ["Microtrader", Microtrader],
        ["Starship", Starship],
        ["Patina Compiler", PatinaCompiler],
        ["MedMaps", MedMaps],
    ];

    const GradientBar = styled(motion.div)`
        height: 30px;
        width: 4px;
        ${props => props.selected ? `background: linear-gradient(to bottom, ${colors[0]}, ${colors[1]})` : ""};
        border-radius: 2px;
        margin-right: ${props => props.selected ? `20px` : "0px"};
        ${props => props.selected ? "animation: animate 0.2s ease-out;" : ""}
        
        @keyframes animate {
            0% {
                margin-right: 0px;
                background: none;
            }
            20% {
                margin-right: 4px;
                background: linear-gradient(to bottom, ${colors[0]}, ${colors[1]});
            }
            to {
                margin-right: 20px;
                background: linear-gradient(to bottom, ${colors[0]}, ${colors[1]});
            }
        }
    `

    const MenuTabItem = ({ id, text }) => {
        const selected = (selectedItem === id);
        const handleClick = () => {
            setRenderAnimation(false);
            setSelectedItem(id);
            setRenderAnimation(true);
        }
        return (
            <MenuItemContainer onClick={handleClick}>
                { selected && <GradientBar selected={selected}/> }
                <MenuText selected={selected}>{text}</MenuText>
            </MenuItemContainer>
        )
    }

  return (
    <Container>
        <MenuContainer>
            <AbsoluteWrapper>
                {MenuItems.map((item, index) => (
                    <MenuTabItem key={index} id={index} text={item[0]} />
                ))}
            </AbsoluteWrapper>
        </MenuContainer>
        <ContentContainer smallDevice={smallDevice}>
            { renderAnimation && (() => {
                const SelectedProject = MenuItems[selectedItem][1];
                return <SelectedProject />;
            })() }
        </ContentContainer>
    </Container>
  )
}
